// ADR 30.9.26l §4.2 — has this browser answered, as React reads it.
//
// A small external store over `hasAnsweredConsent()`, read through
// `useSyncExternalStore` — the shape `LegalStrip.tsx` uses for CMP readiness.
// Only this folder notifies it: a strip answer and a `reopenConsent()`. The
// reconcile never adds the marker, so it cannot change the answer, and an
// `/account` toggle while the strip shows only hides it at the slot's next
// render — harmless, since the gate follows the record, not the strip.
//
// Other tabs are not followed (§4.2): a `storage` listener would be a second
// source of truth for a case that resolves itself on the next load.

import { useSyncExternalStore } from 'react'

import { hasAnsweredConsent } from '../telemetry/consent'
import { forgetConsent } from '../telemetry/consentControl'

/**
 * Set by every strip answer, cleared by a reopen. Where `getItem` works but
 * `setItem` throws (quota, some private modes) the answer cannot be stored,
 * and this is what still hides the strip until the next load.
 */
let answeredInPage = false
const listeners = new Set<() => void>()

function notify(): void {
  for (const listener of listeners) listener()
}

function subscribeConsentAsked(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function consentAnsweredSnapshot(): boolean {
  return answeredInPage || hasAnsweredConsent()
}

/** `useSyncExternalStore`'s server snapshot: nothing to ask during SSR. */
function answeredOnServer(): boolean {
  return true
}

export function useConsentAnswered(): boolean {
  return useSyncExternalStore(subscribeConsentAsked, consentAnsweredSnapshot, answeredOnServer)
}

/** A strip answer landed (or was attempted): hide the strip in this page. */
export function markConsentAnswered(): void {
  answeredInPage = true
  notify()
}

/**
 * §4.3 — "Privacy choices": back to a stranger's clean gate, and ask again.
 *
 * `forgetConsent`, never `withdrawConsent`: the latter `PUT`s, which a
 * signed-out visitor cannot, and writes the marker, which would read as a
 * Decline. `forgetConsent` resets identity, tears both SDKs down locally and
 * puts the record back to the denied default with no marker.
 */
export function reopenConsent(): void {
  forgetConsent()
  answeredInPage = false
  notify()
}

/** Test seam: a page that has answered nothing. */
export function resetConsentAskedForTests(): void {
  answeredInPage = false
}
